import { LiveOrHighlightOrBoth } from '../../../components/result/bestcombination/providerXcompetitonsXgames/types/HighlightOrLiveOrBoth.types';
import { Game } from '../../../interfaces/Games.interface';
import { StreamingOffer } from '../../../interfaces/StreamingOffer.interface';
import { Streamingpackage } from '../../../interfaces/StreamingProvider.interface';
import { comparePrices } from '../prices/comparePrices';

type CoverageResult = {
  providers: Streamingpackage[];
  coveredGames: Game[];
  uncoveredGames: Game[];
  providerPackageCoverage: { [key: string]: Set<number> }; //example: 2 (Magenta): Array[<gameIds>]
};

/**
 * The main function to compute the best combination of streaming providers to cover live streamed games.
 *
 * This function uses the **Greedy Algorithm** to select the optimal combination of providers that maximizes
 * the coverage of live games. The Greedy approach was chosen because it efficiently finds a near-optimal
 * solution by always selecting the provider that covers the most currently uncovered games at each step.
 *
 * @param streamingPackages - Array of streaming package data.
 * @param filteredGames - Array of games from selected teams.
 * @param streamingOffers - Array of streaming offer data.
 * @returns - CoverageResult (includes providers, coveredGames, and uncoveredGames).
 *
 * **Algorithm Explanation**:
 * The Greedy Algorithm was chosen because it:
 * - Prioritizes selecting the provider that covers the maximum number of currently uncovered games.
 * - Provides a solution that is efficient and performs well for most practical cases.
 * - Balances between simplicity and performance, making it suitable for this use case.
 *
 * **Workflow**:
 *  @step1 Extract game IDs from filtered games.
 *  @step2 Create a mapping between game IDs and streaming offers.
 *  @step3 Assign streaming offers to their respective providers.
 *  @step4 Use the Greedy Algorithm to find the best combination of packages that covers the most games.
 *  @step5 Identify games that remain uncovered.
 *  @step6 Select providers based on the best coverage combination.
 */

export function getBestCombi(
  streamingPackages: Streamingpackage[],
  filteredGames: Game[],
  streamingOffers: StreamingOffer[],
  mode: LiveOrHighlightOrBoth
): CoverageResult {
  // Step 1: Extract game IDs from the filtered games
  const filteredGameIds = new Set(filteredGames.map((game) => game.id));

  // Step 2: Create a mapping of game IDs to their respective streaming offers
  const gameToOffers = new Map<number, StreamingOffer[]>();
  streamingOffers.forEach((offer) => {
    const filter =
      mode === 'Live'
        ? offer.live === 1
        : mode === 'Highlight'
          ? offer.highlights === 1
          : offer.highlights === 1 && offer.live === 1;
    if (filteredGameIds.has(offer.game_id.toString()) && filter) {
      const offers = gameToOffers.get(offer.game_id) || [];
      offers.push(offer);
      gameToOffers.set(offer.game_id, offers);
    }
  });

  // Step 3: Map each offer to its associated streaming package
  const packageCoverage: { [key: string]: Set<number> } = {};
  gameToOffers.forEach((offers, gameId) => {
    offers.forEach((offer) => {
      const packageId = offer.streaming_package_id.toString();
      if (!packageCoverage[packageId]) {
        packageCoverage[packageId] = new Set();
      }
      packageCoverage[packageId].add(gameId);
    });
  });

  // Step 4: Apply Greedy Algorithm to find the best combination of packages
  const bestPackages: string[] = [];
  const coveredGames = new Set<number>();
  const gameCoverageDetails: Game[] = [];

  // Sort packages first by the number of games they cover (in descending order),
  // then by price in ascending order
  // FIRST: 2 packages,
  //case 1 both have a monthly_price_cents and a monthly_price_yearly_subscription_in_cents
  //case 2 both have a monthly price_cents and no  monthly_price_yearly_subscription_in_cents
  //case 3 both have no  monthly price_cents and a monthly_price_yearly_subscription_in_cents
  //case 4 one of them has only a monthly price_cents
  //case 5 one of them has only a monthly_price_yearly_subscription_in_cents.

  const sortedPackages = Object.entries(packageCoverage).sort((a, b) => {
    const gameCountComparison = b[1].size - a[1].size;
    if (gameCountComparison !== 0) {
      return gameCountComparison;
    }

    const packageA = streamingPackages.find((pkg) => pkg.id === a[0]);
    const packageB = streamingPackages.find((pkg) => pkg.id === b[0]);

    if (packageA && packageB) {
      return comparePrices(packageA, packageB);
    }

    return 0;
  });
  sortedPackages.forEach(([packageId, gamesCovered]) => {
    // Check for new games that are not yet covered
    const newGames = Array.from(gamesCovered).filter(
      (gameId) => !coveredGames.has(gameId)
    );

    if (newGames.length > 0) {
      bestPackages.push(packageId);
      newGames.forEach((gameId) => coveredGames.add(gameId));

      // Record detailed game coverage information
      newGames.forEach((gameId) => {
        const game = filteredGames.find((g) => g.id === gameId.toString());
        if (game) gameCoverageDetails.push(game);
      });
    }
  });

  // Step 5: Identify games that are not covered by any selected package
  const uncoveredGames = filteredGames.filter(
    (game) => !coveredGames.has(Number(game.id))
  );

  // Step 6: Select the providers that correspond to the chosen best packages and sort descending (coverage)
  const selectedProviders = streamingPackages
    .filter((pkg) => bestPackages.includes(pkg.id.toString()))
    .sort((a, b) => {
      const coverageA = packageCoverage[a.id]?.size || 0;
      const coverageB = packageCoverage[b.id]?.size || 0;
      return coverageB - coverageA; // Descending
    });

  return {
    providers: selectedProviders,
    coveredGames: gameCoverageDetails,
    uncoveredGames: uncoveredGames,
    providerPackageCoverage: packageCoverage, // can be used to determine which provider shows which games
  };
}
